"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@uconnect/assets/lib/utils";
import Icon from '@uconnect/assets/components/ui/icon';
import { cva, type VariantProps } from "class-variance-authority";

function Dialog({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		data-slot="dialog-overlay"
		className={cn(
			"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300 fixed inset-0 z-99999999999 !bg-black/50",
			className
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Variant 'modal' (default) is centered; 'panel' is for sidebar/panel layouts
type DialogContentVariant = 'modal' | 'panel';

const dialogContentVariants = cva(
	"bg-background! data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed z-99999999999 w-full grid gap-4 rounded-lg border p-6 sm:max-w-lg focus-visible:outline-none!",
	{
		variants: {
			variant: {
				modal: "duration-200 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 top-[50%] left-[50%] max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-lg border shadow-lg",
				panel: "bg-white! duration-300 h-dvh top-0 p-0! gap-0! rounded-none! sm:max-w-none! [grid-template-rows:max-content_1fr] custom-scrollbar-container scrollbar--light [&>_section]:contents!",
			},
		},
		defaultVariants: {
			variant: "modal",
		},
	}
);

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content>, VariantProps<typeof dialogContentVariants> {
	showCloseButton?: boolean;
	closeButtonClassName?: string;
	variant?: DialogContentVariant;
}

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	DialogContentProps
>(({
	className,
	children,
	showCloseButton = true,
	closeButtonClassName,
	variant = 'modal',
	...props
}, ref) => (
	<DialogPortal data-slot="dialog-portal">
		<DialogOverlay />
		<DialogPrimitive.Content
			ref={ref}
			data-slot="dialog-content"
			className={cn(
				dialogContentVariants({ variant }),
				className
			)}
			{...props}
		>
			{children}
			{showCloseButton && (
				<DialogPrimitive.Close
					data-slot="dialog-close"
					className={cn(
						"!absolute top-4 right-4 rounded-xs opacity-70 hover:opacity-100 transition-opacity",
						"ring-offset-background focus:ring-0 focus:outline-hidden",
						"focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2",
						"data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
						"cursor-pointer disabled:pointer-events-none",
						"print:hidden",
						"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
						closeButtonClassName
					)}
				>
					<Icon name="xmark" />
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			)}
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="dialog-header"
			className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
			{...props}
		/>
	)
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				"flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
				className
			)}
			{...props}
		/>
	)
}

function DialogTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn("text-lg leading-none font-semibold !m-0", className)}
			{...props}
		/>
	)
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn("text-muted-foreground text-sm !m-0", className)}
			{...props}
		/>
	)
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
